import { useEffect } from 'react';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { useSelectionStore } from '@/store/selectionStore';
import { useCameraStore } from '@/store/cameraStore';
import { CAMERA_LIMITS } from '@/constants/camera';
import { centerOnSelection } from '@/features/camera/cameraNavigation';
import {
  bringSelectionToFront,
  deleteSelection,
  duplicateSelection,
  groupSelection,
  hideSelection,
  invertSelection,
  nudgeSelection,
  selectAllObjects,
  sendSelectionToBack,
  showAll,
  toggleSelectionLocked,
  ungroupSelection,
} from '@/features/objects/objectActions';
import { useEngine } from '@/app/EngineContext';
import { useWindowStore } from '@/store/windowStore';
import { closeWindow, cycleWindows, minimizeWindow, toggleMaximize } from '@/features/windows/windowManager';
import { usePaletteStore } from '@/store/paletteStore';
import { useSettingsStore } from '@/store/settingsStore';
import { zoomToContent, zoomToSelection, zoomToWorkspace } from '@/features/camera/cameraNavigation';
import { addBookmarkCommand } from '@/features/camera/bookmarkCommands';
import { selectActiveDocument } from '@/store/workspaceStore';

const isEditable = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));

/** Shortcuts that work even while typing in an application (⌘K, ⌘W, ⌘M, ⌘`). */
const WINDOW_CHORDS: ReadonlySet<string> = new Set(['k', 'w', 'm', '`', 'p', 'f']);

const ARROWS: Readonly<Record<string, { x: number; y: number }>> = {
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
};

interface Bound {
  readonly undo: () => void;
  readonly redo: () => void;
  readonly zoomIn: () => void;
  readonly zoomOut: () => void;
  readonly zoomReset: () => void;
  readonly fit: () => void;
  readonly centre: () => void;
  readonly group: () => void;
  readonly ungroup: () => void;
  readonly clearSelection: () => void;
  readonly launcher: () => void;
  readonly cycleWindows: () => void;
  readonly closeWindow: () => void;
  readonly minimizeWindow: () => void;
  readonly maximizeWindow: () => void;
  readonly palette: () => void;
  readonly search: () => void;
  readonly zoomSelection: () => void;
  readonly zoomWorkspace: () => void;
  readonly bookmark: () => void;
  readonly toggleGrid: () => void;
  readonly toggleMinimap: () => void;
  readonly toggleDeveloper: () => void;
}

/** Maps a key event to the action it triggers, or null. Pure so the table is easy to audit. */
const resolve = (e: KeyboardEvent, mod: boolean, key: string, b: Bound): (() => void) | null => {
  if (mod && e.shiftKey && key === 'p') return b.palette;
  if (mod && key === 'f') return b.search;
  if (mod && e.shiftKey && key === 'b') return b.bookmark;
  if (mod && e.shiftKey && key === 'd') return b.toggleDeveloper;
  if (mod && e.key === "'") return b.toggleGrid;
  if (mod && key === 'k') return b.launcher;
  if (mod && e.key === '`') return b.cycleWindows;
  if (mod && key === 'w') return b.closeWindow;
  if (mod && e.shiftKey && key === 'm') return b.toggleMinimap;
  if (mod && e.altKey && key === 'm') return b.maximizeWindow;
  if (mod && key === 'm') return b.minimizeWindow;
  if (mod && key === 'z') return b.undo;
  if (mod && key === 'y') return b.redo;
  if (mod && key === 'a') return selectAllObjects;
  if (mod && e.shiftKey && key === 'i') return invertSelection;
  if (mod && key === 'd') return duplicateSelection;
  if (mod && e.shiftKey && key === 'g') return b.ungroup;
  if (mod && key === 'g') return b.group;
  if (mod && e.key === ']') return bringSelectionToFront;
  if (mod && e.key === '[') return sendSelectionToBack;
  if (mod && e.shiftKey && key === 'l') return toggleSelectionLocked;
  if (mod && e.shiftKey && e.altKey && key === 'h') return showAll;
  if (mod && e.shiftKey && key === 'h') return hideSelection;
  if (e.key === 'Delete' || e.key === 'Backspace') return deleteSelection;
  if (e.key === 'Escape') return b.clearSelection;
  const arrow = ARROWS[e.key];
  if (arrow && !mod) {
    const step = e.shiftKey ? 10 : 1;
    return () => {
      nudgeSelection({ x: arrow.x * step, y: arrow.y * step });
    };
  }
  if (mod && (e.key === '=' || e.key === '+')) return b.zoomIn;
  if (mod && e.key === '-') return b.zoomOut;
  if (mod && e.key === '0') return b.zoomReset;
  if (e.shiftKey && !mod && e.code === 'Digit1') return b.fit;
  if (e.shiftKey && !mod && e.code === 'Digit2') return b.zoomSelection;
  if (e.shiftKey && !mod && e.code === 'Digit3') return b.zoomWorkspace;
  if (e.shiftKey && !mod && e.code === 'Digit4') return b.centre;
  return null;
};

/**
 * Global shortcuts. Text inputs are respected: nothing fires while typing.
 *
 *   ⌘Z / ⇧⌘Z / ⌘Y     undo / redo            ⌘A / ⇧⌘I      select all / invert
 *   Delete, Backspace   delete selection        Esc            deselect
 *   ⌘D                  duplicate               ⌘G / ⇧⌘G      group / ungroup
 *   ⌘] / ⌘[             bring to front / back   ⇧⌘L           lock / unlock
 *   ⇧⌘H / ⌥⇧⌘H          hide / show all         arrows (⇧×10)  nudge
 *   ⌘+ / ⌘− / ⌘0        zoom                    ⇧1 / ⇧2       fit / centre selection
 */
export const useKeyboardShortcuts = (): void => {
  const { scene, animator } = useEngine();
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      const key = e.key.toLowerCase();
      // Inside text fields only window-level chords apply; everything else belongs to the field.
      if (isEditable(e.target) && !(mod && WINDOW_CHORDS.has(key))) return;
      const ws = useWorkspaceStore.getState();
      const cam = useCameraStore.getState();
      const handler = resolve(e, mod, key, {
        undo: () => {
          if (e.shiftKey) ws.redo();
          else ws.undo();
        },
        redo: () => {
          ws.redo();
        },
        zoomIn: () => {
          cam.zoomBy(CAMERA_LIMITS.zoomStep);
        },
        zoomOut: () => {
          cam.zoomBy(1 / CAMERA_LIMITS.zoomStep);
        },
        zoomReset: () => {
          cam.setZoom(1);
        },
        fit: () => {
          zoomToContent(scene, animator);
        },
        centre: () => {
          centerOnSelection(scene, animator);
        },
        group: () => {
          groupSelection(scene);
        },
        ungroup: () => {
          ungroupSelection(scene);
        },
        clearSelection: () => {
          useSelectionStore.getState().clear();
        },
        launcher: () => {
          const w = useWindowStore.getState();
          w.setLauncherOpen(!w.launcherOpen);
        },
        cycleWindows: () => {
          cycleWindows(e.shiftKey ? -1 : 1);
        },
        closeWindow: () => {
          const id = useWindowStore.getState().activeWindowId;
          if (id) closeWindow(id);
        },
        minimizeWindow: () => {
          const id = useWindowStore.getState().activeWindowId;
          if (id) minimizeWindow(id);
        },
        maximizeWindow: () => {
          const id = useWindowStore.getState().activeWindowId;
          if (id) toggleMaximize(id);
        },
        palette: () => {
          const p = usePaletteStore.getState();
          if (p.open) p.hide();
          else p.show('commands');
        },
        search: () => {
          usePaletteStore.getState().show('search');
        },
        zoomSelection: () => {
          zoomToSelection(scene, animator);
        },
        zoomWorkspace: () => {
          zoomToWorkspace(scene, animator);
        },
        bookmark: () => {
          const d = selectActiveDocument(ws);
          if (d) ws.dispatch(addBookmarkCommand(d, cam.camera, `View ${d.workspace.bookmarks.length + 1}`));
        },
        toggleGrid: () => {
          useSettingsStore.getState().setGridVisible(!useSettingsStore.getState().gridVisible);
        },
        toggleMinimap: () => {
          useSettingsStore.getState().setMinimapVisible(!useSettingsStore.getState().minimapVisible);
        },
        toggleDeveloper: () => {
          useSettingsStore.getState().setDeveloperMode(!useSettingsStore.getState().developerMode);
        },
      });
      if (handler) {
        e.preventDefault();
        handler();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [scene, animator]);
};
